// BulkInvitePage.jsx — /spaces/:spaceId/invite/bulk
// v0.6 — 2026-08-30 (DF-7 PR2): warm roster restyle — copy "Invite people to
//   contribute to {name}'s space"; per-row avatar initial; .rowSelected sage-tint on
//   checked; email shown in parens beside the editable name. Presentation/copy only.
// v0.5 — NEW-13, two defects, both fixed:
//        (a) the email->status lookup was a Map built last-wins, so on an
//            address with several rows (invite/revoke/re-invite) a stale
//            'revoked' row could mask the live 'active' one - the guard then
//            allowed a re-invite and produced yet another duplicate. Now
//            keeps the strongest status per email (active > pending > revoked).
//        (b) a manually typed email is now checked against existing
//        active/pending contributors. Rows added via addRow previously
//        bypassed the alreadyIn guard entirely (it is only computed at
//        roster-build time), so inviting an address that is already an
//        active contributor silently created a duplicate contributor row.
//        Revoked addresses stay invitable - re-inviting is legitimate.
//        NEW-24: name + email now sit side by side (CSS only).
// v0.4 — B3.3: Cancel button below Send (returns to feed).
// v0.3 — B3.2: Select all / Deselect all toggle (eligible rows only; default off).
// v0.2 — B3.1: roster = union(participants, submitted emails), exclude owner (self).
// v0.1 — Group Spaces B3: invite several people at once (flag-gated, dark).
//
// Reached from a flag-gated link on InvitePage (space view) and from the
// LeaderPage dashboard space-picker. Distinct from the single InvitePage.
// Backend: POST /spaces/:id/invite/bulk (owner-only; max 100; per-email status;
// first invite flips a private space to shared). This page is a UI gate only.
//
// Roster (best-effort; all three tolerate failure -> manual entry still works):
//   universe   -> GET /leader/emails            { emails:[{email}] }
//   names      -> GET /leader/dashboard         { participants:[{name,email}] }
//   already-in -> GET /spaces/:id/contributors  { contributors:[{email,status}] }
//
// Copy: approved brand-voice set (2026-07-26).

import { useState, useEffect, useCallback, useRef } from 'react';
import { buildStatusByEmail, isAlreadyInSpace, statusNoticeFor, lookupStatus } from '../utils/contributorStatus';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppContext } from '../App';
import { createApiClient } from '../api/client';
import LovedOneBar from '../components/LovedOneBar';
import { useResolvedPhotoUrl } from '../hooks/useResolvedPhotoUrl';
import styles from './BulkInvitePage.module.css';

// A participant's display name falls back to their email server-side; treat an
// email-shaped name as "no real name" so the leader is asked to fill it.
function realName(name, email) {
  if (!name) return '';
  if (email && name.trim().toLowerCase() === email.trim().toLowerCase()) return '';
  return name;
}

export default function BulkInvitePage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const appState = useAppContext();

  const [space, setSpace] = useState(null);
  const resolvedPhotoUrl = useResolvedPhotoUrl(space, getAccessTokenSilently);

  const [rows, setRows] = useState([]); // { key, email, name, selected, alreadyIn, statusLabel, editableEmail }
  // NEW-13: lowercased email -> existing contributor status, filled by load().
  const statusByEmailRef = useRef(new Map());
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null); // { invited, total, results } | null
  const [error, setError] = useState(null);

  // ── Load space + roster (roster best-effort) ───────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const api = createApiClient(getAccessTokenSilently);
      try {
        const spaceData = await api.get(`/spaces/${spaceId}`);
        if (!cancelled) setSpace(spaceData);
      } catch (err) {
        console.error('Bulk invite: space load failed', err);
      }

      const [emailsRes, dashRes, contribRes] = await Promise.allSettled([
        api.get('/leader/allowlist'),
        api.get('/leader/dashboard'),
        api.get(`/spaces/${spaceId}/contributors`),
      ]);
      if (cancelled) return;

      const emails = emailsRes.status === 'fulfilled' ? (emailsRes.value?.emails || []) : [];
      const participants = dashRes.status === 'fulfilled' ? (dashRes.value?.participants || []) : [];
      const contributors = contribRes.status === 'fulfilled' ? (contribRes.value?.contributors || []) : [];

      const lc = (s) => String(s || '').toLowerCase();
      const ownerEmail = lc(appState?.email);
      // NEW-13: strongest-status-per-email (see utils/contributorStatus.js).
      const statusByEmail = buildStatusByEmail(contributors);
      statusByEmailRef.current = statusByEmail;

      // B3.1: union — participants (name+email) first, then submitted emails not
      // already present. Exclude the owner (self); dedup by lowercased email.
      const byEmail = new Map();
      for (const p of participants) {
        const email = lc(p.email);
        if (!email || email === ownerEmail) continue;
        byEmail.set(email, { email, name: realName(p.name, p.email) });
      }
      for (const e of emails) {
        const email = lc(e.email);
        if (!email || email === ownerEmail || byEmail.has(email)) continue;
        byEmail.set(email, { email, name: e.name || '' });
      }

      const built = Array.from(byEmail.values()).map((r, i) => {
        const status = statusByEmail.get(r.email);
        const alreadyIn = isAlreadyInSpace(status);
        return {
          key: `r${i}`,
          email: r.email,
          name: r.name,
          selected: false,
          alreadyIn,
          statusLabel: statusNoticeFor(status),
          editableEmail: false,
        };
      });

      setRows(built);
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [spaceId, getAccessTokenSilently]);

  // ── Row helpers ────────────────────────────────────────────────────────
  const toggleRow = useCallback((key) => {
    setRows((rs) => rs.map((r) => (r.key === key && !r.alreadyIn ? { ...r, selected: !r.selected } : r)));
  }, []);
  const setRowName = useCallback((key, name) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, name } : r)));
  }, []);
  const setRowEmail = useCallback((key, email) => {
    // NEW-13: roster rows get their guard at build time; hand-added rows need
    // it applied as the address is typed, or a duplicate slips through.
    const status = lookupStatus(statusByEmailRef.current, email);
    const alreadyIn = isAlreadyInSpace(status);
    const statusLabel = statusNoticeFor(status);
    setRows((rs) => rs.map((r) => (
      r.key === key
        ? { ...r, email, alreadyIn, statusLabel, selected: alreadyIn ? false : r.selected }
        : r
    )));
  }, []);
  const addRow = useCallback(() => {
    setRows((rs) => [...rs, {
      key: `n${Date.now()}`, email: '', name: '', selected: true,
      alreadyIn: false, statusLabel: '', editableEmail: true,
    }]);
  }, []);
  // B3.2: select all / deselect all — toggles only eligible (non-greyed) rows.
  const toggleSelectAll = useCallback((checked) => {
    setRows((rs) => rs.map((r) => (r.alreadyIn ? r : { ...r, selected: checked })));
  }, []);

  const selected = rows.filter((r) => r.selected && !r.alreadyIn);
  const eligibleCount = rows.filter((r) => !r.alreadyIn).length;
  const allSelected = eligibleCount > 0 && selected.length === eligibleCount;
  const missingName = selected.some((r) => !r.name.trim());
  const missingEmail = selected.some((r) => !r.email.trim());
  const canSend = selected.length > 0 && !missingName && !missingEmail;

  const isPrivate = space?.privacyMode === 'private';

  // ── Send ───────────────────────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    if (!canSend || sending) return;
    setSending(true);
    setError(null);
    try {
      const api = createApiClient(getAccessTokenSilently);
      const res = await api.post(`/spaces/${spaceId}/invite/bulk`, {
        invites: selected.map((r) => ({ email: r.email.trim(), contributorName: r.name.trim() })),
        message: message.trim() || null,
      });
      setResults(res);
      setConfirmOpen(false);
    } catch (err) {
      console.error('Bulk invite send failed', err);
      setError("We couldn't send these just now. Please try again when you're ready.");
      setConfirmOpen(false);
    } finally {
      setSending(false);
    }
  }, [canSend, sending, selected, message, spaceId, getAccessTokenSilently]);

  if (loading) {
    return <div className={styles.loading}><div className={styles.loadingDot} /><span>Loading...</span></div>;
  }

  // ── Results view ─────────────────────────────────────────────────────────
  if (results) {
    const byStatus = (st) => results.results.filter((r) => r.status === st);
    const invited = byStatus('invited');
    const active = byStatus('already_active');
    const failed = [...byStatus('invalid'), ...byStatus('error'), ...byStatus('limit_reached')];
    return (
      <div className={styles.screen}>
        <LovedOneBar
          spaceName={space?.name}
          spacePhotoUrl={resolvedPhotoUrl}
          subtitle="Invite people"
          onBack={() => navigate(`/spaces/${spaceId}`)}
        />
        <div className={styles.content}>
          {invited.length > 0 && (
            <div className={styles.resultGood}>
              {invited.length} {invited.length === 1 ? 'invitation is' : 'invitations are'} on their way.
              Each person can add a memory whenever they're ready.
            </div>
          )}
          {active.length > 0 && (
            <div className={styles.resultNote}>
              {active.length} {active.length === 1 ? 'person is' : 'people are'} already in this space, so we didn't send a new invitation.
            </div>
          )}
          {failed.length > 0 && (
            <div className={styles.resultWarn}>
              We couldn't send a few of these. Please check their details and try again when you're ready.
            </div>
          )}
          <button className={styles.btnPrimary} onClick={() => navigate(`/spaces/${spaceId}`)}>Done</button>
        </div>
      </div>
    );
  }

  // ── Main view ─────────────────────────────────────────────────────────────
  return (
    <div className={styles.screen}>
      <LovedOneBar
        spaceName={space?.name}
        spacePhotoUrl={resolvedPhotoUrl}
        subtitle="Invite people"
        onBack={() => navigate(`/spaces/${spaceId}`)}
      />

      <div className={styles.content}>
        <div className={styles.formCard}>
          <h3 className={styles.formTitle}>Invite people to contribute to {space?.name}'s space</h3>
          <p className={styles.formDesc}>Choose who you'd like to invite to contribute to this space. You are able to invite others not listed as well.</p>

          {rows.length === 0 && (
            <p className={styles.empty}>No one to invite here yet. You can add people by email below.</p>
          )}

          {eligibleCount > 0 && (
            <button type="button" className={styles.selectAll} onClick={() => toggleSelectAll(!allSelected)}>
              {allSelected ? 'Deselect all' : 'Select all'}
            </button>
          )}

          <div className={styles.rosterList}>
            {rows.map((r) => {
              const initial = (r.name || r.email || '?').trim().charAt(0).toUpperCase();
              return (
              <div key={r.key} className={`${styles.row} ${r.selected && !r.alreadyIn ? styles.rowSelected : ''} ${r.alreadyIn ? styles.rowDisabled : ''}`}>
                <label className={styles.rowCheck}>
                  <input
                    type="checkbox"
                    checked={r.selected}
                    disabled={r.alreadyIn}
                    onChange={() => toggleRow(r.key)}
                  />
                </label>
                <span className={styles.rowAvatar} aria-hidden="true">{initial}</span>
                <div className={styles.rowFields}>
                  <input
                    className={styles.rowName}
                    type="text"
                    placeholder="Their name"
                    value={r.name}
                    maxLength={100}
                    disabled={r.alreadyIn}
                    onChange={(e) => setRowName(r.key, e.target.value)}
                  />
                  {r.editableEmail
                    ? <input
                        className={styles.rowEmailInput}
                        type="email"
                        placeholder="email@example.com"
                        value={r.email}
                        onChange={(e) => setRowEmail(r.key, e.target.value)}
                      />
                    : <span className={styles.rowEmailParen}>({r.email})</span>}
                </div>
                {r.alreadyIn && <span className={styles.rowAnnotation}>{r.statusLabel}</span>}
              </div>
              );
            })}
          </div>

          <button type="button" className={styles.addRow} onClick={addRow}>Add someone else</button>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Personal note <span className={styles.optional}>(optional)</span></label>
            <textarea
              className={styles.fieldTextarea}
              rows={3}
              maxLength={500}
              placeholder={`e.g., I've been keeping some memories of ${space?.name || 'them'} here...`}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          {missingName && selected.length > 0 && (
            <div className={styles.inlineHint}>Please add their name, so we can greet them in the invitation.</div>
          )}
          {error && <div className={styles.errorBanner}>{error}</div>}

          <button
            className={styles.btnPrimary}
            disabled={!canSend}
            onClick={() => setConfirmOpen(true)}
          >
            {selected.length > 0
              ? `Invite ${selected.length} ${selected.length === 1 ? 'person' : 'people'}`
              : 'Invite people'}
          </button>

          {/* B3.3: Cancel below the send button — returns to the feed. */}
          <button type="button" className={styles.cancelLink} onClick={() => navigate(`/spaces/${spaceId}`)}>
            Cancel
          </button>
        </div>

        <div className={styles.privacyNote}>
          <span>🔒</span>
          <span>Only the people you invite can see this space.</span>
        </div>
      </div>

      {/* Confirm */}
      {confirmOpen && (
        <div className={styles.modalOverlay} onClick={() => setConfirmOpen(false)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>
              You're about to invite {selected.length} {selected.length === 1 ? 'person' : 'people'} to {space?.name}.
            </h3>
            {isPrivate && (
              <p className={styles.modalBody}>
                When you invite people, this space becomes shared with everyone you invite, so they can add
                their own memories. Only the people you invite can see it.
              </p>
            )}
            <div className={styles.modalActions}>
              <button className={styles.btnCancel} onClick={() => setConfirmOpen(false)}>Not yet</button>
              <button className={styles.btnPrimary} disabled={sending} onClick={handleSend}>
                {sending ? 'Sending...' : 'Send invitations'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
